import { createContext, RefObject, useEffect, useRef, useState } from "react";
import { debounce } from "lodash";
import useResizeObserver from "use-resize-observer";

export type RankifierDestination = {
  ref: RefObject<HTMLDivElement>;
  toRender: number;
};

export type RenderableDestinations = "deck" | "hand" | "selected";

export type RankifierDestinationsContextType = {
  refs: {
    [key in RenderableDestinations]: RankifierDestination;
  };
  resizeClock: number;
};

export const RankifierDesinationsContext = createContext<RankifierDestinationsContextType | null>(null);

interface RankifierDestinationsProviderProps {
  children: React.ReactNode;
  cardsInDeck: number;
  cardsPerHand: number;
  cardsInSelected: number;
}

export const RankifierDestinationsProvider = (props: RankifierDestinationsProviderProps) => {
  const { children, cardsInDeck, cardsPerHand, cardsInSelected } = props;

  const { ref: resizeObserverRef, width, height } = useResizeObserver<HTMLDivElement>();
  const [resizeClock, setResizeClock] = useState(0);
  const updateClock = debounce(() => setResizeClock((prev) => prev + 1), 200);

  const deckRef = useRef<HTMLDivElement>(null);
  const handRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);

  useEffect(() => updateClock(), [width, height]);

  return (
    <RankifierDesinationsContext.Provider
      value={{
        refs: {
          deck: {
            ref: deckRef,
            toRender: cardsInDeck,
          },
          hand: {
            ref: handRef,
            toRender: cardsPerHand,
          },
          selected: {
            ref: selectedRef,
            toRender: cardsInSelected,
          },
        },
        resizeClock,
      }}
    >
      <div
        ref={resizeObserverRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -1,
        }}
      />
      {children}
    </RankifierDesinationsContext.Provider>
  );
};
